// Shared editorial body renderer used by ArticleDetailPage and OurStoryPage.
// Detects section headings, styles byline / headings / paragraphs consistently.

// Strip document-level tags the publishing pipeline sometimes embeds in article
// HTML (<html>/<head>/<title>/<body>/<!doctype>) — a stray <title> inside body
// content is hoisted to <head> by browsers and would create duplicate page titles.
export function sanitizeArticleHtml(html) {
  return String(html || "")
    .replace(/<!doctype[^>]*>/gi, "")
    .replace(/<head[^>]*>[\s\S]*?<\/head>/gi, "")
    .replace(/<title[^>]*>[\s\S]*?<\/title>/gi, "")
    .replace(/<\/?(html|body)[^>]*>/gi, "");
}

export const BODY_CONTAINER = { maxWidth: '720px', margin: '0 auto', padding: '2rem 1.5rem 0' };

export const BYLINE_STYLE = {
  fontSize: '0.875rem',
  color: '#5C5344',
  textTransform: 'uppercase',
  letterSpacing: '0.05em',
  marginBottom: '2rem',
  fontWeight: 500,
  borderLeft: '3px solid #C9A96E',
  paddingLeft: '0.9rem',
};

export const HEADING_STYLE = {
  fontSize: '1.5rem',
  fontWeight: 700,
  color: '#1B2F5E',
  marginTop: '2.5rem',
  marginBottom: '1rem',
  letterSpacing: '-0.01em',
  lineHeight: 1.25,
};

export const PARAGRAPH_STYLE = {
  fontSize: '1.125rem',
  lineHeight: 1.8,
  color: '#2D3748',
  marginBottom: '1.5rem',
};

export const BODY_SCOPED_CSS = `
  .sm-article-body p { font-size: 1.125rem; line-height: 1.8; color: #2D3748; margin-bottom: 1.5rem; }
  .sm-article-body h2 { font-family: 'Playfair Display', ui-serif, Georgia, serif; font-size: 1.5rem; font-weight: 700; color: #1B2F5E; margin-top: 2.5rem; margin-bottom: 1rem; letter-spacing: -0.01em; line-height: 1.25; }
  .sm-article-body h3 { font-family: 'Playfair Display', ui-serif, Georgia, serif; font-size: 1.25rem; font-weight: 700; color: #1B2F5E; margin-top: 2rem; margin-bottom: 0.75rem; }
  .sm-article-body ul, .sm-article-body ol { margin: 1rem 0 1.5rem 1.25rem; color: #2D3748; font-size: 1.125rem; line-height: 1.8; }
  .sm-article-body ul { list-style: disc; }
  .sm-article-body ol { list-style: decimal; }
  .sm-article-body aside.corrections-note, .sm-article-body .bookshop-affiliate { max-width: 100%; box-sizing: border-box; overflow-wrap: break-word; }
  .sm-article-body .bookshop-affiliate img, .sm-article-body aside.corrections-note img { max-width: 100%; height: auto; }
  .sm-article-body blockquote { border-left: 3px solid #C9A96E; padding: 0.25rem 0 0.25rem 1rem; margin: 1.5rem 0; color: #4A5568; font-style: italic; }
  .sm-article-body a { color: #1B2F5E; text-decoration: underline; text-underline-offset: 3px; }
  .sm-article-body a:hover { color: #C9A96E; }
  .sm-article-body strong { color: #1B2F5E; font-weight: 700; }
  .sm-article-body hr { border: 0; border-top: 1px solid rgba(27, 47, 94, 0.15); margin: 2.5rem 0; }
`;

export function looksLikeHeading(text) {
  const t = text.trim();
  if (!t) return false;
  if (t.length > 90) return false;
  if (t.includes('\n')) return false;
  const words = t.split(/\s+/);
  if (words.length < 1 || words.length > 8) return false;
  if (/[.!?,;:]$/.test(t)) return false;
  if (words.length === 1 && /^(and|or|but|the|of|in|on|to|for|by|a|an)$/i.test(words[0])) return false;
  return true;
}

/**
 * Return React nodes for `text`, linking the FIRST occurrence of
 * "SummaryMaps" or "Summary Maps" to https://summarymaps.com.
 * `state.linked` is a shared mutable flag so callers can enforce
 * "only one link per paragraph" across multiple lines.
 */
const SM_LINK_STYLE = { color: '#E8952A', textDecoration: 'none', fontWeight: 'inherit' };
const SM_RE = /SummaryMaps|Summary Maps/;

function linkifySummaryMaps(text, state, keyPrefix = 'sm') {
  if (!text) return text;
  if (state.linked) return text;
  const match = text.match(SM_RE);
  if (!match) return text;
  const before = text.slice(0, match.index);
  const hit = match[0];
  const after = text.slice(match.index + hit.length);
  state.linked = true;
  return [
    before,
    <a
      key={`${keyPrefix}-link`}
      href="https://summarymaps.com"
      target="_blank"
      rel="noopener noreferrer"
      style={SM_LINK_STYLE}
      onMouseEnter={(e) => { e.currentTarget.style.textDecoration = 'underline'; }}
      onMouseLeave={(e) => { e.currentTarget.style.textDecoration = 'none'; }}
      data-testid="summarymaps-link"
    >
      {hit}
    </a>,
    after,
  ];
}

/**
 * Render plain-text article body. First block is treated as a byline;
 * short standalone lines become <h2>; everything else becomes <p>.
 * If skipByline=true, the first block is treated as a normal paragraph
 * (useful when the page already has its own hero title/byline).
 */
export function renderPlainTextArticle(body, { skipByline = false } = {}) {
  const blocks = (body || '').split(/\n\n+/).map((b) => b.trim()).filter(Boolean);
  return blocks.map((block, i) => {
    if (!skipByline && i === 0) {
      return (
        <p key={i} style={BYLINE_STYLE} data-testid="article-byline">
          {block}
        </p>
      );
    }
    if (looksLikeHeading(block)) {
      const state = { linked: false };
      return (
        <h2 key={i} className="font-serif" style={HEADING_STYLE} data-testid="article-heading">
          {linkifySummaryMaps(block, state, `h2-${i}`)}
        </h2>
      );
    }
    const lines = block.split('\n');
    const state = { linked: false }; // one link max per paragraph
    return (
      <p key={i} style={PARAGRAPH_STYLE}>
        {lines.map((line, j) => (
          <span key={j}>
            {linkifySummaryMaps(line, state, `p-${i}-${j}`)}
            {j < lines.length - 1 ? <br /> : null}
          </span>
        ))}
      </p>
    );
  });
}
